import { SEO } from "@/components/SEO";
import { PageSchema } from "@/components/seo/PageSchema";
import { LastUpdated } from "@/components/seo/LastUpdated";
import { TopCTA } from "@/components/cta/TopCTA";
import { MidPageCTA } from "@/components/cta/MidPageCTA";
import { BottomCTA } from "@/components/cta/BottomCTA";
import { FAQSection } from "@/components/content/FAQSection";
import { InternalLinks } from "@/components/content/InternalLinks";
import { TemplateBlock } from "@/components/content/TemplateBlock";
import { Checklist } from "@/components/content/Checklist";
import { DIYvsDoneForYou } from "@/components/content/DIYvsDoneForYou";
import { CalendlyEmbed } from "@/components/CalendlyEmbed";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { MapPin, FileText, Search, Star, Phone } from "lucide-react";

const faqs = [
  {
    question: "How many service area pages should I create?",
    answer: "Create pages for every city and major neighborhood you actively serve. Start with 5-10 of your most important areas, then expand. Quality over quantity—thin pages hurt more than help."
  },
  {
    question: "Is this duplicate content if the pages are similar?",
    answer: "No, if you customize each page with city-specific content: local landmarks, neighborhoods, unique services for that area, and relevant testimonials. Avoid copy-pasting the same content."
  },
  {
    question: "Should service area pages have unique content?",
    answer: "Yes! Each page needs 400-800 words of unique content. Mention specific neighborhoods, local references, and testimonials from customers in that area when possible."
  },
  {
    question: "How do I structure URLs for service area pages?",
    answer: "Use /[city]-lawn-care/ or /lawn-care-in-[city]/ format. Keep URLs clean and include the main keyword. Avoid overly long URLs with multiple keywords."
  }
];

const ServiceAreaPageTemplate = () => {
  const pageUrl = "https://www.leafrushmarketing.com/website-templates/service-area-page-template/";
  
  return (
    <>
      <SEO
        title="Service Area Page Template | Local SEO for Lawn Care"
        description="Free service area page template for local SEO. Rank for 'lawn care in [city]' searches with this proven structure."
        url={pageUrl}
        keywords="service area page template, local SEO pages, lawn care city pages"
      />
      <PageSchema
        pageUrl={pageUrl}
        pageName="Service Area Page Template"
        pageDescription="Local SEO template for service area pages that help you rank in more cities."
        faqs={faqs}
      />

      <TopCTA />

      <section className="py-16 md:py-24">
        <div className="container px-4 md:px-6">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <Badge variant="secondary">Website Template</Badge>
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight">
              Service Area{" "}
              <span className="text-primary">Page Template</span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Create city and neighborhood pages that rank for "[service] in [city]" 
              searches. Expand your local SEO footprint.
            </p>
            <LastUpdated />
          </div>
        </div>
      </section>

      <section className="py-12">
        <div className="container px-4 md:px-6">
          <div className="max-w-4xl mx-auto space-y-8">
            <h2 className="text-2xl font-bold">Service Area Page Structure</h2>
            
            <div className="grid gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Search className="h-5 w-5 text-primary" />
                    Section 1: SEO-Optimized Header
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <TemplateBlock
                    title="H1 Headline Templates"
                    content={`Lawn Care in [City] – Professional Lawn Services Near You

OR

[City] Lawn Care Services – Locally Owned & Operated

OR

Professional Landscaping in [City], [State]

Examples:
• Lawn Care in Round Rock – Professional Lawn Services
• Pflugerville Landscaping – Design, Installation & Maintenance
• Cedar Park Lawn Mowing & Yard Care Services`}
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MapPin className="h-5 w-5 text-primary" />
                    Section 2: Local Introduction
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <TemplateBlock
                    title="Opening Paragraph Template"
                    content={`Looking for reliable lawn care in [City]? [Company Name] has been serving [City] homeowners and businesses for [X] years. We know [City] lawns—from the [soil type/climate challenges] to the best times for [seasonal service].

Whether you're in [Neighborhood 1], [Neighborhood 2], or near [Local Landmark], we provide consistent, professional lawn care you can count on.`}
                  />
                  <Checklist
                    title="Local Elements to Include"
                    items={[
                      "Specific neighborhood names",
                      "Local landmarks or streets",
                      "Climate/soil references for the area",
                      "Years serving that specific city",
                      "Distance from your base location"
                    ]}
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5 text-primary" />
                    Section 3: Services in This Area
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <TemplateBlock
                    title="Services Section"
                    content={`Our [City] Lawn Care Services

We offer comprehensive lawn care throughout [City]:

• Weekly Lawn Mowing – Starting at $[X]
• Fertilization & Weed Control – [X]-step program
• Aeration & Overseeding – Best in [season]
• Spring & Fall Cleanup
• Shrub & Hedge Trimming
• [Any area-specific service]

[View All Services] [Get Free Quote for [City]]`}
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Star className="h-5 w-5 text-primary" />
                    Section 4: Local Testimonials
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <TemplateBlock
                    title="Testimonial Section"
                    content={`What [City] Customers Say

"[Testimonial text that mentions the city or neighborhood]"
– [Name], [City] Homeowner

"[Another local testimonial]"
– [Name], [Neighborhood] Resident

[See More Reviews on Google]`}
                  />
                  <p className="text-muted-foreground text-sm">
                    Use testimonials from customers in that specific city/area when possible. 
                    If not available, use reviews that mention the broader region.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MapPin className="h-5 w-5 text-primary" />
                    Section 5: Neighborhoods We Serve
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <TemplateBlock
                    title="Neighborhoods Section"
                    content={`[City] Neighborhoods We Serve

We provide lawn care throughout [City], including:

• [Neighborhood 1]
• [Neighborhood 2]
• [Neighborhood 3]
• [Neighborhood 4]
• [Subdivision Name]
• Areas near [School/Park/Landmark]

Not sure if we serve your area? Call [phone] or request a quote—we'll confirm availability.`}
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Phone className="h-5 w-5 text-primary" />
                    Section 6: Local CTA
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <TemplateBlock
                    title="Bottom CTA"
                    content={`Get Lawn Care in [City] Today

Ready for a better lawn? [Company Name] serves [City] and surrounding areas with professional, reliable lawn care.

[Get Free Quote] [Call (XXX) XXX-XXXX]

Also serving: [Nearby City 1] | [Nearby City 2] | [Nearby City 3]`}
                  />
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <MidPageCTA />

      <section className="py-12">
        <div className="container px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold mb-6">DIY vs. Done-For-You</h2>
            <DIYvsDoneForYou
              diyPoints={[
                "Create pages for each service area",
                "Write unique content for each city",
                "Time: 2-3 hours per page",
                "Research local details for authenticity"
              ]}
              doneForYouPoints={[
                "We research and write all city pages",
                "SEO-optimized with local keywords",
                "10-20 service area pages included",
                "Ongoing expansion as you grow"
              ]}
            />
          </div>
        </div>
      </section>

      <section className="py-12">
        <div className="container px-4 md:px-6">
          <div className="max-w-2xl mx-auto">
            <InternalLinks
              title="Related Templates & Resources"
              links={[
                { to: "/service-area-pages-local-seo/", label: "Service Area Pages for Local SEO" },
                { to: "/website-templates/lawn-care-service-page-template/", label: "Lawn Care Service Page Template" },
                { to: "/website-templates/lawn-care-website-seo-checklist/", label: "SEO Checklist" },
                { to: "/website-templates/", label: "All Templates" }
              ]}
            />
          </div>
        </div>
      </section>

      <FAQSection faqs={faqs} />
      <BottomCTA />
      <CalendlyEmbed />
    </>
  );
};

export default ServiceAreaPageTemplate;
